import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, iif, of } from 'rxjs';
import { map, mergeMap, withLatestFrom, catchError } from 'rxjs/operators';

/* notification service */
import { NotificationService } from 'src/app/features/shared/services/notification-service/notification.service';

/* jsonata object class */
import { JSONAtaObject } from 'src/app/features/shared/helper-classes';

/* lodash lib */
import * as lodash from 'lodash';

/* DataStorage interface for ngrx */
import { DataStorage } from 'src/app/features/data-stores/data-storage.interface';

/* navigate action */
import { Navigate } from 'src/app/features/data-stores/router-data-store/state/router-data-store.actions';

/* Workflow Management API Service */
import { WorkflowManagementControllerService } from 'src/app/features/shared/sdk/services/workflow-management-controller.service';

/* application-data-store actions */
import * as ApplicationDataStoreActions from 'src/app/features/data-stores/application-data-store/state/application-data-store.actions';

/* router-data-store actions */
import * as RouterDataStoreActions from 'src/app/features/data-stores/router-data-store/state/router-data-store.actions';

/* api services */
import { ApplicationControllerService } from 'src/app/features/shared/sdk/services';

@Injectable()
export class ApplicationDataStoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store<DataStorage>,
    private notificationService: NotificationService,
    private workflowManagementApi: WorkflowManagementControllerService,
    private applicationApiService: ApplicationControllerService,
  ) { 
    // prettier-ignore
    // #region customCode: data-store-effects__constructor__start
    
    // #endregion customCode: data-store-effects__constructor__start

    // prettier-ignore
    // #region customCode: data-store-effects__constructor__end
    
    // #endregion customCode: data-store-effects__constructor__end
  }

  updateActiveApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicationDataStoreActions.UpdateActiveApplication),
      withLatestFrom(
        this.store.select(
          (state) => state.ApplicationDataStore.activeApplication,
        ),
        this.store.select((state) => state.ApplicationDataStore.newApplication),
      ),
      /* dataset */
      mergeMap(([action, activeApplication, newApplication]) => {
        const data = {
          eventPayload: action['payload'] ?? {},
          activeApplication,
          newApplication,
        };
        const initialPayload = of(JSONAtaObject.chain(data));
        return initialPayload;
      }),

      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__start

      // #endregion customCode: data-store-effects__pre-data-transformation__start

      /* pre data transformation */
      mergeMap(payloadAsJsonataObj => {
            /* Pre Data Transformation Step 1 ( Map ) */
            /* get value of the passed jsonata object */
            const data = <any>payloadAsJsonataObj.value;
            /* transform data */
            const transformedData = JSONAtaObject.chain(data).transform(`
              
          {
              "newApplication": $merge([newApplication, eventPayload.newApplication])
          }
            `);
            return transformedData;
          }),
      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__end

      // #endregion customCode: data-store-effects__pre-data-transformation__end
      /* trigger success actions */
      mergeMap((jsonataObj) => {
            /* get value of the passed jsonata object */
            const data = <any>jsonataObj.value;
            return [
              ApplicationDataStoreActions.UpdateActiveApplicationSuccessful({ payload: data }),
              ];
          }),
    ),
  );

  applicationCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicationDataStoreActions.CreateApplicationSuccessful),
      /* dataset */
      mergeMap((action) => {
        const data = action['payload'] ?? {};
        const initialPayload = of(JSONAtaObject.chain(data));
        return initialPayload;
      }),

      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__start

      // #endregion customCode: data-store-effects__pre-data-transformation__start

      /* pre data transformation */
      mergeMap(payloadAsJsonataObj => {
            /* Pre Data Transformation Step 1 ( Map ) */
            /* get value of the passed jsonata object */
            const data = <any>payloadAsJsonataObj.value;
            /* transform data */
            const transformedData = JSONAtaObject.chain(data).transform(`
              { 
              "path": "/download-app-page"
          }
            `);
            return transformedData;
          }),
      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__end

      // #endregion customCode: data-store-effects__pre-data-transformation__end
      /* trigger success actions */
      mergeMap((jsonataObj) => {
            /* get value of the passed jsonata object */
            const data = <any>jsonataObj.value;
            return [
              RouterDataStoreActions.Navigate({ payload: data }),
              ];
          }),
    ),
  );

  getApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicationDataStoreActions.GetApplications),
      mergeMap((action) => {
        return this.applicationApiService.find().pipe(
          mergeMap((applications) => {
            const payload: any = {
              applications: applications,
            };
            return [
              ApplicationDataStoreActions.GetApplicationsSuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while fetching data.',
            );
            return [ApplicationDataStoreActions.GetApplicationsFailure()];
          }),
        );
      }),
    ),
  );

  getApplicationById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicationDataStoreActions.GetApplicationById),
      mergeMap((action) => {
        return this.applicationApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((application) => {
              const payload: any = {
                application: application,
              };
              return [
                ApplicationDataStoreActions.GetApplicationByIdSuccessful({
                  payload,
                }),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [ApplicationDataStoreActions.GetApplicationByIdFailure()];
            }),
          );
      }),
    ),
  );

  createApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicationDataStoreActions.CreateApplication),
      withLatestFrom(
        this.store.select((state) => state.ApplicationDataStore.newApplication),
        this.store.select((state) => state.AuthDataStore.user),
      ),
      /* dataset */
      mergeMap(([action, newApplication, user]) => {
        const data = {
          eventPayload: action['payload'] ?? {},
          newApplication,
          user,
        };
        const initialPayload = of(JSONAtaObject.chain(data));
        return initialPayload;
      }),

      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__start

      // #endregion customCode: data-store-effects__pre-data-transformation__start

      /* pre data transformation */
      mergeMap(payloadAsJsonataObj => {
            /* Pre Data Transformation Step 1 ( Map ) */
            /* get value of the passed jsonata object */
            const data = <any>payloadAsJsonataObj.value;
            /* transform data */
            const transformedData = JSONAtaObject.chain(data).transform(`
              {
              "body": $merge([newApplication, {"userId": user.id}]) 
          }
            `);
            return transformedData;
          }),
      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__end

      // #endregion customCode: data-store-effects__pre-data-transformation__end
      /* process */
      mergeMap(payloadAsJsonataObj => {
            /* get value of the passed jsonata object */
            const data = <any>payloadAsJsonataObj.value;

            return this.applicationApiService.create(data).pipe(
                mergeMap(response => {
                  return of({
                    success: true,
                    data: response
                  });
                }),
                catchError((error) => {
                  return of({
                    success: false,
                    data: error
                  });
                })
              );

            }),
      /* trigger actions */
      mergeMap((result) =>
        iif(
          () => result.success,
          /* success case */
          of(result.data).pipe(
            /* convert to jsonata object */
            mergeMap((data) => {
              return of(JSONAtaObject.chain(data));
            }),
            /* success actions */
            mergeMap((jsonataObj) => {
              /* get value of the passed jsonata object */
              const data = <any>jsonataObj.value;
              return [
                ApplicationDataStoreActions.CreateApplicationSuccessful({
                  payload: data,
                }),
              ];
            }),
          ),
          /* failure case */
          of(result.data).pipe(
            /* convert to jsonata object */
            mergeMap((data) => {
              return of(JSONAtaObject.chain(data));
            }),
            /* failure actions */
            mergeMap((jsonataObj) => {
              /* get value of the passed jsonata object */
              const data = <any>jsonataObj.value;
              return [ApplicationDataStoreActions.CreateApplicationFailure()];
            }),
          ),
        ),
      ),
    ),
  );

  updateApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicationDataStoreActions.UpdateApplication),
      mergeMap((action) => {
        const params = {
          id: action.payload.id,
          body: action.payload.data,
        };
        return this.applicationApiService.updateById(<any>params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been updated successfully.',
            );
            return [ApplicationDataStoreActions.GetApplications()];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while updating the data.',
            );
            return [ApplicationDataStoreActions.UpdateApplicationFailure()];
          }),
        );
      }),
    ),
  );

  deleteApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApplicationDataStoreActions.DeleteApplication),
      mergeMap((action) => {
        const params = { id: action.payload.id };
        return this.applicationApiService.deleteById(params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'Your data has been deleted successfully.',
            );
            const payload: any = {
              id: action.payload.id,
            };
            return [
              ApplicationDataStoreActions.DeleteApplicationSuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while deleting the data.',
            );
            return [ApplicationDataStoreActions.DeleteApplicationFailure()];
          }),
        );
      }),
    ),
  );
}
